import { DATAMAP_TYPES, DatamapDoc, DatamapIssue, DatamapIssueField, DatamapRow, DatamapType } from "types/datamap.types"

/** The largest category code: the engine keeps 65535 for "no answer". */
export const MAX_CODE = 65534
const CODE_PATTERN = /^\d+$/

/** A number and its categories may share one data column: "Clarity - value,INT,A10" next to "Clarity,CAT,A10". */
export const isCodedTwin = (rows: DatamapRow[]): boolean =>
  rows.length === 2 && rows.some((row) => row.type === "CAT") && rows.some((row) => row.type === "INT")

/**
 * Every problem that would make the import drop or misread a row. The editor refuses to save,
 * and the upload refuses the file, while any remain. Messages name the label and the code.
 */
export const validateDatamap = (doc: DatamapDoc): DatamapIssue[] => {
  const issues: DatamapIssue[] = []
  const add = (row: DatamapRow, field: DatamapIssueField, message: string): void => {
    issues.push({ rowId: row.id, field, message })
  }

  const labels = new Set(doc.rows.map((row) => row.label).filter((label) => label !== ""))
  const firstWithLabel = new Map<string, DatamapRow>()
  const rowsByVariable = new Map<string, DatamapRow[]>()

  for (const row of doc.rows) {
    if (row.label === "") {
      add(row, "label", "The label is empty.")
    } else if (row.label.split("//").some((part) => part === "")) {
      add(row, "label", 'The label has an empty part between "//" separators.')
    } else {
      if (firstWithLabel.has(row.label)) {
        add(row, "label", `Another row already uses the label "${row.label}"; only one of them would import.`)
      } else {
        firstWithLabel.set(row.label, row)
      }
      const parts = row.label.split("//")
      for (let n = 1; n < parts.length; n++) {
        const group = parts.slice(0, n).join("//")
        if (labels.has(group)) {
          add(
            row,
            "label",
            `The label starts with "${group}//", but "${group}" is a row of its own. A label can't be a question and a group, so one of them would not import.`
          )
          break
        }
      }
    }

    if (!DATAMAP_TYPES.includes(row.type as DatamapType)) {
      add(row, "type", `The type "${row.type}" is not one of ${DATAMAP_TYPES.join(", ")}.`)
    }

    if (row.variable === "") {
      add(row, "variable", "The variable is empty, so no data column can match it.")
    } else {
      rowsByVariable.set(row.variable, [...(rowsByVariable.get(row.variable) ?? []), row])
    }

    if (row.type === "CAT") validateCodes(row, add)
  }

  for (const [variable, rows] of rowsByVariable) {
    if (rows.length === 1 || isCodedTwin(rows)) continue
    const firstLabel = rows[0].label || "(no label)"
    for (const row of rows.slice(1)) {
      add(row, "variable", `The variable "${variable}" is also used by "${firstLabel}".`)
    }
  }

  return issues
}

const validateCodes = (
  row: DatamapRow,
  add: (row: DatamapRow, field: DatamapIssueField, message: string) => void
): void => {
  if (row.values.length === 0) {
    add(row, "values", "A category question needs at least one code and label.")
    return
  }
  const labelOfCode = new Map<string, string>()
  const codeOfLabel = new Map<string, string>()
  for (const value of row.values) {
    if (value.code === "" && value.label === "") {
      add(row, "values", "A code and label pair is empty; fill it in or remove it.")
      continue
    }
    if (value.code === "") {
      add(row, "values", `The label "${value.label}" has no code.`)
      continue
    }
    if (!CODE_PATTERN.test(value.code) || Number(value.code) > MAX_CODE) {
      add(row, "values", `Code "${value.code}" must be a whole number from 0 to ${MAX_CODE}.`)
      continue
    }
    const code = String(Number(value.code))
    if (value.label === "") {
      add(row, "values", `Code ${code} has no label.`)
      continue
    }
    const other = labelOfCode.get(code)
    if (other !== undefined) add(row, "values", `Code ${code} is used twice ("${other}" and "${value.label}").`)
    else labelOfCode.set(code, value.label)
    const otherCode = codeOfLabel.get(value.label)
    if (otherCode !== undefined) add(row, "values", `The label "${value.label}" is used for codes ${otherCode} and ${code}.`)
    else codeOfLabel.set(value.label, code)
  }
}
